<template>
  <div class="quiz-completion-record-page questionnaire-theme">
    <van-loading v-if="loading && !quiz.id" class="loading-center" />

    <div v-else-if="invalidQuery" class="state-wrapper">
      <van-empty description="缺少 quizId、objType 或 objId" />
    </div>

    <div v-else-if="loadError" class="state-wrapper">
      <van-empty description="加载失败，请稍后重试" />
    </div>

    <div v-else-if="!hasCompletionRecord" class="state-wrapper">
      <van-empty description="暂无完成记录" />
    </div>

    <template v-else>
      <div class="quiz-main">
        <div class="qn-title-block">
          <i class="qn-title-accent" aria-hidden="true" />
          <h1 class="qn-title-text">{{ quizDisplayTitle }}</h1>
        </div>

        <div class="record-meta-card">
          <div class="record-meta">完成时间：{{ formatTime(completionRecord.completedAt) }}</div>
          <div class="record-meta">
            作答结果：{{ completionRecord.allQuestionsCorrect ? '全部正确' : '存在错误' }}
          </div>
          <div v-if="!quiz.isAnswerOpen" class="record-tip">
            当前考试已关闭作答，仅支持查看当前记录
          </div>
        </div>

        <div class="record-content">
          <DynamicQuizPaper
            :paper-config="quiz.paperConfig || {}"
            :answer-config="quiz.answerConfig || {}"
            :value="completionRecord.answerContent || {}"
            :readonly="true"
          />
        </div>

        <div v-if="canRedo" class="footer-actions">
          <van-button
            block
            type="primary"
            round
            class="redo-btn"
            @click="redoQuiz"
          >
            重新作答
          </van-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { Loading, Empty, Button } from 'vant'
import { setTitle } from '@/libs/ds-bridge'
import { getQuizDetail, getCurrentCompletionRecord } from '@/api/quiz'
import DynamicQuizPaper from './components/DynamicQuizPaper'

Vue.use(Loading).use(Empty).use(Button)

export default {
  name: 'QuizCompletionRecord',
  components: { DynamicQuizPaper },
  data() {
    return {
      quizId: '',
      objType: '',
      objId: '',
      returnUrl: '',
      quiz: {},
      hasCompletionRecord: false,
      completionRecord: {},
      loading: false,
      loadError: false
    }
  },
  computed: {
    invalidQuery() {
      return !this.quizId || !this.objType || !this.objId
    },
    quizDisplayTitle() {
      return this.quiz.title || '考试详情'
    },
    canRedo() {
      return this.hasCompletionRecord && this.quiz.isAnswerOpen === true
    }
  },
  watch: {
    '$route.query': {
      handler(query) {
        this.quizId = query.quizId || ''
        this.objType = query.objType || ''
        this.objId = query.objId || ''
        this.returnUrl = query.returnUrl || ''
        if (!this.invalidQuery) {
          this.loadPage()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.syncEmbeddedTitle()
  },
  methods: {
    syncEmbeddedTitle() {
      document.title = '调研问卷'
      setTitle('调研问卷')
    },
    routeQuery(extra = {}) {
      const base = {
        quizId: this.quizId,
        objType: this.objType,
        objId: this.objId,
        ...extra
      }
      if (this.returnUrl) {
        base.returnUrl = this.returnUrl
      }
      return base
    },
    loadPage() {
      this.loading = true
      this.loadError = false
      this.quiz = {}
      this.hasCompletionRecord = false
      this.completionRecord = {}
      Promise.all([
        getQuizDetail(this.quizId, { objType: this.objType, objId: this.objId }),
        getCurrentCompletionRecord(this.quizId, { objType: this.objType, objId: this.objId })
      ])
        .then(([quizRes, recordRes]) => {
          if (!(quizRes && recordRes)) {
            this.loadError = true
            return
          }
          const quizData = this.unwrapResponse(quizRes)
          const recordData = this.unwrapResponse(recordRes)
          this.quiz = quizData || {}
          this.hasCompletionRecord = recordData && recordData.hasCompletionRecord === true
          this.completionRecord = (recordData && recordData.completionRecord) || {}
          this.syncEmbeddedTitle()
        })
        .catch(() => {
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    redoQuiz() {
      this.$router.replace({
        path: '/quiz/play',
        query: this.routeQuery({ forcePlay: '1' })
      }).catch(() => {})
    },
    unwrapResponse(response) {
      return (response && typeof response === 'object' && ('data' in response)) ? response.data : response
    },
    formatTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    }
  }
}
</script>

<style lang="scss" scoped>
.quiz-completion-record-page {
  min-height: 100vh;
  padding-bottom: calc(90px + constant(safe-area-inset-bottom));
  padding-bottom: calc(90px + env(safe-area-inset-bottom));
  background: #f5f6f7;
}

.quiz-main {
  padding: 12px 16px 0;
  box-sizing: border-box;
}

.loading-center {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.state-wrapper {
  padding-top: 40px;
}

.record-meta-card {
  margin: 0 0 16px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
}

.record-meta {
  margin-top: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: #646566;
}

.record-tip {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff7e6;
  color: #8a4b00;
  font-size: 13px;
}

.record-content {
  padding: 0;
}

.footer-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 12px 16px;
  padding-bottom: calc(12px + constant(safe-area-inset-bottom));
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
}

.redo-btn {
  height: 44px;
  border: none;
  background: var(--qn-theme);
}
</style>
